import { ArrowLeftOutlined } from '@ant-design/icons';
import { Alert, Button } from 'antd';
import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
    DOCUMENT_LINK_PROVIDER,
    getExternalDocumentById,
    getDocumentOverrideById,
    googleSheetsViewerEmbedUrl,
    isGoogleSheetsWorkbookUrl,
    resolveDocumentViewUrl,
} from '../../config/externalDocuments';

type RouteParams = { docId: string };

const preventContextMenu = (e: React.MouseEvent) => e.preventDefault();

/** Sandbox only for spreadsheets — Chrome PDF viewer needs an unsandboxed iframe for correct fill */
const VIEWER_IFRAME_SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-presentation';

const isOneDriveEmbedUrl = (url: string) =>
    /1drv\.ms\/x\//i.test(url) && (/[?&]em=2/i.test(url) || /wdHideGridlines|wdHideHeaders|wdAllowInteractivity/i.test(url));

const DocumentViewer: FunctionComponent = () => {
    const { docId } = useParams<RouteParams>();
    const history = useHistory();
    const { t } = useTranslation(['f', 'b']);

    const doc = useMemo(() => getExternalDocumentById(docId), [docId]);
    const url = doc ? resolveDocumentViewUrl(doc) : null;
    const overrideTitle = doc ? getDocumentOverrideById(doc.id)?.title?.trim() : '';
    const displayTitle = overrideTitle || (doc?.title ? doc.title : doc?.titleKey ? t(doc.titleKey) : '');

    const iframeSrc = useMemo(() => {
        if (!url || !doc) {
            return null;
        }
        // OneDrive embed URLs already point to embeddable workbook view.
        if (isOneDriveEmbedUrl(url)) {
            return url;
        }
        if (DOCUMENT_LINK_PROVIDER === 'google' && doc.kind === 'spreadsheet') {
            return googleSheetsViewerEmbedUrl(url);
        }
        return url;
    }, [doc, url]);

    const showDriveExcelHint =
        !!doc &&
        !!url &&
        DOCUMENT_LINK_PROVIDER === 'google' &&
        doc.kind === 'spreadsheet' &&
        !isOneDriveEmbedUrl(url) &&
        !isGoogleSheetsWorkbookUrl(url);

    if (!doc) {
        return (
            <div className="documents-page documents-page--with-app-header" onContextMenu={preventContextMenu}>
                <div className="documents-page__inner">
                    <Alert type="warning" message={t('f:documents.missingDocument')} showIcon />
                    <Button type="link" onClick={() => history.push('/documents')} className="documents-page__back">
                        {t('f:documents.backToList')}
                    </Button>
                </div>
            </div>
        );
    }

    if (!url) {
        return (
            <div className="documents-page documents-page--with-app-header" onContextMenu={preventContextMenu}>
                <div className="documents-page__inner">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => history.push('/documents')}
                        className="documents-page__toolbar-btn"
                    >
                        {t('f:documents.backToList')}
                    </Button>
                    <Alert
                        type="info"
                        message={t('f:documents.notConfiguredTitle')}
                        description={t('f:documents.notConfiguredHint')}
                        showIcon
                        className="documents-page__alert"
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="documents-page documents-page--viewer documents-page--with-app-header" onContextMenu={preventContextMenu}>
            <div className="documents-page__toolbar">
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => history.push('/documents')}
                    className="documents-page__toolbar-btn"
                >
                    {t('f:documents.backToList')}
                </Button>
                <span className="documents-page__toolbar-title">{displayTitle}</span>
            </div>
            {showDriveExcelHint ? (
                <div className="documents-page__sheet-hint">
                    <Alert type="warning" message={t('f:documents.driveExcelHintTitle')} description={t('f:documents.driveExcelHintBody')} showIcon />
                </div>
            ) : null}
            <div className={`documents-page__frame-wrap${doc.kind === 'pdf' ? ' documents-page__frame-wrap--pdf' : ''}`}>
                {!showDriveExcelHint ? (
                    <iframe
                        title={displayTitle}
                        src={iframeSrc || url}
                        className="documents-page__iframe"
                        allow="fullscreen"
                        {...(doc.kind === 'spreadsheet'
                            ? { sandbox: VIEWER_IFRAME_SANDBOX }
                            : {})}
                        referrerPolicy="strict-origin-when-cross-origin"
                    />
                ) : null}
                {!showDriveExcelHint && doc.kind === 'spreadsheet' && (
                    <div className="documents-page__share-blocker" aria-hidden="true" />
                )}
            </div>
        </div>
    );
};

export default DocumentViewer;
